import React, { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logDev } from "@/lib/logger";
import SEO from "@/components/SEO";
import { CheckCircle2, XCircle, ShieldCheck, Star, Bell, TrendingUp, Sparkles, X } from "lucide-react";

// Badge classes per moderation status (default = pending/other)
const STATUS_BADGE = {
  approved: "bg-green-100 text-green-700 border-green-300",
  rejected: "bg-red-100 text-red-700 border-red-300",
  pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
};
const statusBadgeCls = (status, fallback = "bg-red-100 text-red-700 border-red-300") =>
  STATUS_BADGE[status] || fallback;

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("properties");
  const [stats, setStats] = useState({});
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [legal, setLegal] = useState([]);
  const [support, setSupport] = useState([]);
  const [docs, setDocs] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [unlockReqs, setUnlockReqs] = useState([]);

  const refreshAll = async () => {
    const [s, p, l, b, lg, sp, dc, se, rq, ur] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/properties"),
      api.get("/admin/leads"),
      api.get("/admin/brokers"),
      api.get("/admin/legal-requests"),
      api.get("/admin/support"),
      api.get("/admin/docs"),
      api.get("/admin/sellers"),
      api.get("/admin/requirements"),
      api.get("/admin/unlock-requests"),
    ]);
    setStats(s.data); setProperties(p.data.items); setLeads(l.data.items);
    setBrokers(b.data.items); setLegal(lg.data.items);
    setSupport(sp.data.items); setDocs(dc.data.items); setSellers(se.data.items);
    setRequirements(rq.data.items);
    setUnlockReqs(ur.data.items);
  };

  useEffect(() => { if (user?.role === "admin") refreshAll(); }, [user]);

  if (loading) return <div className="container-tharlo py-20">Loading...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "admin") return <div className="container-tharlo py-20 text-center">Admin access only.</div>;

  const update = async (id, patch) => {
    await api.patch(`/admin/properties/${id}`, patch);
    toast.success("Updated");
    refreshAll();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/admin/properties/${id}`);
    toast.success("Deleted");
    refreshAll();
  };

  const tabs = [
    { k: "properties", label: `Properties (${stats.properties_total ?? 0})` },
    { k: "leads", label: `Leads (${stats.leads_total ?? 0})` },
    { k: "requirements", label: `Requirements (${requirements.length})` },
    { k: "unlocks", label: `Unlock Requests (${unlockReqs.length})` },
    { k: "brokers", label: `Brokers (${stats.brokers_total ?? 0})` },
    { k: "legal", label: `Legal (${stats.legal_requests_total ?? 0})` },
    { k: "sellers", label: `Sellers (${sellers.length})` },
    { k: "docs", label: `Seller Docs (${docs.length})` },
    { k: "support", label: `Support (${support.length})` },
  ];

  return (
    <div className="container-tharlo py-12" data-testid="admin-dashboard">
      <SEO title="Admin Console" description="Tharlo internal admin console." noindex />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-3">Admin · Tharlo</div>
          <h1 className="font-display text-4xl text-ink tracking-tight">Dashboard</h1>
        </div>
        <NotificationBell onNav={setTab} />
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Properties" value={stats.properties_total} />
        <Stat label="Pending Review" value={stats.properties_pending} accent />
        <Stat label="New Leads" value={stats.leads_new} accent />
        <Stat label="Total Users" value={stats.users_total} />
      </div>

      <UnlockAnalyticsWidget />

      <div className="mt-10 border-b border-border flex gap-1 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={()=>setTab(t.k)}
            className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab===t.k?"border-gold text-ink":"border-transparent text-muted-foreground hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "properties" && <PropertiesTab properties={properties} onUpdate={update} onRemove={remove} />}

        {tab === "leads" && <LeadsTab leads={leads} />}

        {tab === "requirements" && <RequirementsTab requirements={requirements} />}

        {tab === "unlocks" && <UnlocksTab items={unlockReqs} onChanged={refreshAll} />}

        {tab === "brokers" && <BrokersTab brokers={brokers} />}

        {tab === "legal" && <LegalTab legal={legal} />}

        {tab === "sellers" && <SellersTab sellers={sellers} />}

        {tab === "docs" && <DocsTab docs={docs} sellers={sellers} />}

        {tab === "support" && <SupportTab support={support} />}
      </div>
    </div>
  );
}

/* ============ Admin tab sections ============ */

function PropertiesTab({ properties, onUpdate, onRemove }) {
  return (
    <div className="space-y-3">
      {properties.map((p) => (
        <div key={p.id} className="bg-white border border-border p-4 flex flex-col lg:flex-row gap-4 items-start" data-testid={`admin-prop-${p.id}`}>
          <img src={p.images?.[0] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200"} alt="" className="w-full lg:w-32 aspect-[4/3] object-cover" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`trust-badge ${statusBadgeCls(p.status)}`}>{p.status}</span>
              {p.verified && <span className="trust-badge bg-gold text-white border-gold"><ShieldCheck className="w-3 h-3" />Verified</span>}
              {p.featured && <span className="trust-badge bg-ink text-white border-ink"><Star className="w-3 h-3" />Featured</span>}
            </div>
            <h4 className="font-display text-lg text-ink">{p.title}</h4>
            <div className="text-xs text-muted-foreground">{p.locality}, {p.city} · {formatINR(p.price)} · by {p.owner_name}</div>
            {p.premium_justification && (
              <details className="mt-2 text-xs" data-testid={`premium-note-${p.id}`}>
                <summary className="cursor-pointer text-gold font-semibold hover:underline">📝 Seller&apos;s Premium Justification (internal)</summary>
                <p className="mt-2 p-3 bg-muted/50 border-l-2 border-gold whitespace-pre-wrap text-foreground/80">{p.premium_justification}</p>
              </details>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {p.status !== "approved" && <button data-testid={`approve-${p.id}`} onClick={()=>onUpdate(p.id, {status:"approved"})} className="text-xs px-3 py-2 bg-green-600 text-white hover:bg-green-700"><CheckCircle2 className="w-3 h-3 inline mr-1" />Approve</button>}
            {p.status !== "rejected" && <button onClick={()=>onUpdate(p.id, {status:"rejected"})} className="text-xs px-3 py-2 bg-red-600 text-white hover:bg-red-700"><XCircle className="w-3 h-3 inline mr-1" />Reject</button>}
            <button onClick={()=>onUpdate(p.id, {verified: !p.verified})} className="text-xs px-3 py-2 border border-gold text-gold hover:bg-gold hover:text-white">{p.verified?"Unverify":"Verify"}</button>
            <button onClick={()=>onUpdate(p.id, {featured: !p.featured})} className="text-xs px-3 py-2 border border-ink text-ink hover:bg-ink hover:text-white">{p.featured?"Unfeature":"Feature"}</button>
            <button onClick={()=>onRemove(p.id)} className="text-xs px-3 py-2 text-red-600 hover:underline">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadsTab({ leads }) {
  return (
    <table className="w-full text-sm bg-white border border-border">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Budget</th><th className="p-3">Purpose</th><th className="p-3">Location</th><th className="p-3">Date</th></tr>
      </thead>
      <tbody>
        {leads.map((l)=>(
          <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
            <td className="p-3 font-medium">{l.name}</td>
            <td className="p-3">{l.phone}</td>
            <td className="p-3">{l.budget}</td>
            <td className="p-3">{l.purpose}</td>
            <td className="p-3">{l.preferred_location}</td>
            <td className="p-3 text-xs">{l.created_at?.slice(0,10)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RequirementsTab({ requirements }) {
  const budgetText = (r) => {
    if (!r.budget_min && !r.budget_max) return "—";
    return `${r.budget_min ? formatINR(r.budget_min) : "0"} – ${r.budget_max ? formatINR(r.budget_max) : "∞"}`;
  };
  return (
    <table className="w-full text-sm bg-white border border-border" data-testid="admin-requirements-table">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr>
          <th className="p-3">Buyer</th>
          <th className="p-3">Contact</th>
          <th className="p-3">Type</th>
          <th className="p-3">Location</th>
          <th className="p-3">Budget</th>
          <th className="p-3">Timeline</th>
          <th className="p-3">Notes</th>
          <th className="p-3">Date</th>
        </tr>
      </thead>
      <tbody>
        {requirements.length === 0 ? (
          <tr><td className="p-6 text-center text-muted-foreground" colSpan={8}>No requirements posted yet.</td></tr>
        ) : requirements.map((r) => (
          <tr key={r.id} className="border-t border-border align-top" data-testid={`req-row-${r.id}`}>
            <td className="p-3 font-medium">{r.user_name || r.name || "Guest"}</td>
            <td className="p-3 text-xs">
              <div>{r.user_phone || r.phone || "—"}</div>
              <div className="text-muted-foreground">{r.user_email || r.email || ""}</div>
            </td>
            <td className="p-3"><span className="trust-badge">{r.property_type}</span>{r.listing_type !== "any" && <div className="text-xs mt-1">{r.listing_type}</div>}</td>
            <td className="p-3 text-xs">{r.locality ? `${r.locality}, ` : ""}{r.city}</td>
            <td className="p-3 text-xs">{budgetText(r)}</td>
            <td className="p-3 text-xs">{r.timeline || "—"}</td>
            <td className="p-3 text-xs max-w-[300px] whitespace-pre-wrap">{r.notes || "—"}</td>
            <td className="p-3 text-xs">{r.created_at?.slice(0, 10)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UnlocksTab({ items, onChanged }) {
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/unlock-requests/${id}`, { status });
      toast.success("Status updated");
      onChanged();
    } catch { toast.error("Failed to update"); }
  };
  return (
    <table className="w-full text-sm bg-white border border-border" data-testid="admin-unlocks-table">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr>
          <th className="p-3">Buyer</th>
          <th className="p-3">Contact</th>
          <th className="p-3">Report</th>
          <th className="p-3">Property</th>
          <th className="p-3">Status</th>
          <th className="p-3">Requested</th>
          <th className="p-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr><td className="p-6 text-center text-muted-foreground" colSpan={7}>No unlock requests yet.</td></tr>
        ) : items.map((u) => (
          <tr key={u.id} className="border-t border-border align-top" data-testid={`unlock-row-${u.id}`}>
            <td className="p-3 font-medium">{u.user_name || "—"}</td>
            <td className="p-3 text-xs">
              <div>{u.user_phone || "—"}</div>
              <div className="text-muted-foreground">{u.user_email || ""}</div>
            </td>
            <td className="p-3"><span className="trust-badge bg-gold/10 border-gold/40 text-ink">{u.report_label}</span></td>
            <td className="p-3 text-xs">
              <a href={`/property/${u.property_slug || u.property_id}`} target="_blank" rel="noreferrer" className="text-gold hover:underline">{u.property_title || u.property_id}</a>
            </td>
            <td className="p-3">
              <select
                data-testid={`unlock-status-${u.id}`}
                value={u.status}
                onChange={(e) => setStatus(u.id, e.target.value)}
                className="text-xs border border-border px-2 py-1 outline-none focus:border-ink"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </td>
            <td className="p-3 text-xs">{u.created_at?.slice(0, 10)}</td>
            <td className="p-3 text-xs">
              <a href={`https://wa.me/${(u.user_phone||"").replace(/\D/g,"")}?text=${encodeURIComponent(`Hi ${u.user_name}, following up on your Tharlo ${u.report_label} request for ${u.property_title}.`)}`} target="_blank" rel="noreferrer" className="text-gold hover:underline">WhatsApp</a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BrokersTab({ brokers }) {
  return (
    <table className="w-full text-sm bg-white border border-border">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr><th className="p-3">Name</th><th className="p-3">Agency</th><th className="p-3">Phone</th><th className="p-3">Email</th><th className="p-3">Locations</th><th className="p-3">Exp.</th></tr>
      </thead>
      <tbody>
        {brokers.map((b)=>(
          <tr key={b.id} className="border-t border-border">
            <td className="p-3 font-medium">{b.name}</td>
            <td className="p-3">{b.agency}</td>
            <td className="p-3">{b.phone}</td>
            <td className="p-3">{b.email}</td>
            <td className="p-3">{b.operating_locations}</td>
            <td className="p-3">{b.experience_years}y</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LegalTab({ legal }) {
  return (
    <table className="w-full text-sm bg-white border border-border">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Service</th><th className="p-3">Property</th><th className="p-3">Date</th></tr>
      </thead>
      <tbody>
        {legal.map((l)=>(
          <tr key={l.id} className="border-t border-border">
            <td className="p-3 font-medium">{l.name}</td>
            <td className="p-3">{l.phone}</td>
            <td className="p-3">{l.service}</td>
            <td className="p-3 text-xs">{l.property_address}</td>
            <td className="p-3 text-xs">{l.created_at?.slice(0,10)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SellersTab({ sellers }) {
  return (
    <table className="w-full text-sm bg-white border border-border">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Role</th><th className="p-3">Properties</th><th className="p-3">Score</th><th className="p-3">Tier</th></tr>
      </thead>
      <tbody>
        {sellers.map((s)=>(
          <tr key={s.user_id} className="border-t border-border">
            <td className="p-3 font-medium">{s.full_name || s.name}</td>
            <td className="p-3 text-xs">{s.email}</td>
            <td className="p-3 text-xs">{s.phone || '-'} {s.phone_verified && <span className="text-green-600">✓</span>}</td>
            <td className="p-3"><span className="trust-badge">{s.seller_type || s.role}</span></td>
            <td className="p-3">{s.property_count}</td>
            <td className="p-3">{s.profile?.score}%</td>
            <td className="p-3 text-xs">{s.profile?.tier}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DocsTab({ docs, sellers }) {
  return (
    <table className="w-full text-sm bg-white border border-border">
      <thead className="bg-muted text-left text-xs uppercase tracking-widest">
        <tr><th className="p-3">User</th><th className="p-3">Kind</th><th className="p-3">Status</th><th className="p-3">Uploaded</th><th className="p-3">Action</th></tr>
      </thead>
      <tbody>
        {docs.map((d)=>{
          const seller = sellers.find(s => s.user_id === d.user_id);
          return (
            <tr key={d.id} className="border-t border-border">
              <td className="p-3">{seller?.full_name || seller?.name || d.user_id}</td>
              <td className="p-3 uppercase text-xs">{d.kind.replace('_',' ')}</td>
              <td className="p-3"><span className={`trust-badge ${statusBadgeCls(d.status, "bg-yellow-100 text-yellow-700 border-yellow-300")}`}>{d.status}</span></td>
              <td className="p-3 text-xs">{d.created_at?.slice(0,10)}</td>
              <td className="p-3"><a href={`${process.env.REACT_APP_BACKEND_URL}${d.download_url}`} target="_blank" rel="noreferrer" className="text-gold hover:underline text-xs">Download</a></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SupportTab({ support }) {
  return (
    <div className="space-y-3" data-testid="admin-support-list">
      {support.length === 0 ? <p className="text-muted-foreground text-sm">No tickets yet.</p> : support.map((t)=>(
        <div key={t.id} className="bg-white border border-border p-5" data-testid={`admin-support-${t.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {t.tag && <code className="text-xs font-mono font-bold text-ink bg-gold/10 border border-gold/40 px-2 py-0.5" data-testid={`admin-support-tag-${t.id}`}>{t.tag}</code>}
                <span className="trust-badge bg-yellow-100 text-yellow-700 border-yellow-300">{t.status}</span>
              </div>
              <div className="font-semibold text-ink">{t.subject}</div>
              <div className="text-xs text-muted-foreground">From: {t.user_name} · {t.user_email} · {t.created_at?.slice(0,10)}</div>
            </div>
          </div>
          <p className="text-sm text-foreground/80 whitespace-pre-wrap">{t.message}</p>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-5 border ${accent?"bg-gold/10 border-gold/30":"bg-white border-border"}`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-3xl text-ink mt-1">{value ?? 0}</div>
    </div>
  );
}

/* ============ Notification Bell ============ */
const TAB_FOR_TYPE = {
  lead: "leads",
  requirement: "requirements",
  unlock_request: "unlocks",
  broker_registration: "brokers",
  legal_request: "legal",
};

function NotificationBell({ onNav }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const dropRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/notifications?limit=30");
      setItems(r.data.items || []);
      setUnseen(r.data.unseen || 0);
    } catch (err) { logDev.error("notification refresh failed:", err); }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000); // poll every minute
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllSeen = async () => {
    try {
      await api.post("/admin/notifications/mark-seen", {});
      refresh();
    } catch (err) { logDev.error("mark-seen failed:", err); }
  };

  const handleClick = (n) => {
    setOpen(false);
    const tab = TAB_FOR_TYPE[n.type];
    if (tab && onNav) onNav(tab);
  };

  return (
    <div ref={dropRef} className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open && unseen) markAllSeen(); }}
        data-testid="admin-notification-bell"
        aria-label={`${unseen} unread notifications`}
        className="relative p-3 border border-border bg-white hover:border-ink transition-colors"
      >
        <Bell className="w-5 h-5 text-ink" />
        {unseen > 0 && (
          <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-gold text-ink text-[11px] font-bold flex items-center justify-center border border-ink">
            {unseen > 9 ? "9+" : unseen}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute right-0 top-full mt-2 w-[360px] max-h-[500px] bg-white border border-border shadow-2xl z-40 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div>
              <div className="font-display text-lg text-ink">Notifications</div>
              <div className="text-xs text-muted-foreground">{items.length ? `Showing latest ${items.length}` : "You\u2019re all caught up"}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <Bell className="w-8 h-8 text-gold mx-auto mb-2 opacity-40" />
                No activity yet.
              </div>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left p-4 border-b border-border hover:bg-muted transition-colors ${!n.seen ? "bg-gold/5" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.seen && <span className="w-2 h-2 bg-gold rounded-full mt-2 shrink-0" aria-label="unread" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-ink truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{n.subtext}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Most-Requested Reports This Week (Analytics Widget) ============ */
function UnlockAnalyticsWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/unlock-week")
      .then((r) => setData(r.data))
      .catch(() => setData({ total_this_week: 0, by_report: [], top_properties: [] }));
  }, []);

  if (!data) return null;

  // Week-over-week % change; if no previous-week data, treat any activity as +100%
  const computeWow = () => {
    if (data.total_previous_week) {
      return Math.round(((data.total_this_week - data.total_previous_week) / data.total_previous_week) * 100);
    }
    return data.total_this_week > 0 ? 100 : 0;
  };
  const wow = computeWow();

  return (
    <div className="mt-6 bg-ink text-white p-6 lg:p-8" data-testid="unlock-analytics-widget">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="overline text-gold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Analytics · Last 7 days</div>
          <h2 className="font-display text-2xl text-white mt-1">Most-requested Tharlo reports</h2>
          <p className="text-xs text-white/60 mt-1">Where paid demand is landing — use this to prioritise your on-ground verification team.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-white/60">Total this week</div>
          <div className="font-display text-3xl text-gold">{data.total_this_week}</div>
          {data.total_previous_week > 0 && (
            <div className={`text-xs mt-0.5 flex items-center justify-end gap-1 ${wow >= 0 ? "text-green-400" : "text-red-400"}`}>
              <TrendingUp className={`w-3 h-3 ${wow < 0 ? "rotate-180" : ""}`} /> {wow > 0 ? "+" : ""}{wow}% vs prev week
            </div>
          )}
        </div>
      </div>

      {data.total_this_week === 0 ? (
        <div className="text-sm text-white/60 border-t border-white/10 pt-4">No unlock requests yet in the last 7 days.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/10 pt-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">By report type</div>
            <div className="space-y-2">
              {data.by_report.map((r) => {
                const pct = data.total_this_week ? Math.round((r.count / data.total_this_week) * 100) : 0;
                return (
                  <div key={r.report} data-testid={`analytics-report-${r.report}`}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-white">{r.label}</span>
                      <span className="font-semibold text-gold tabular-nums">{r.count}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 mt-1.5">
                      <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">Top 5 properties</div>
            <div className="space-y-2">
              {data.top_properties.map((p, i) => (
                <div key={p.property_id} className="flex items-baseline gap-3" data-testid={`analytics-prop-${p.property_id}`}>
                  <div className="w-6 text-xs text-white/60 tabular-nums">#{i + 1}</div>
                  <Link to={`/property/${p.slug || p.property_id}`} target="_blank" className="text-sm text-white hover:text-gold truncate flex-1">{p.title}</Link>
                  <div className="text-sm font-semibold text-gold tabular-nums">{p.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
